import React, { useState } from 'react';
import {
  X, Plus, Trash2, Save, PlayCircle, Copy, BookOpen, FilePlus2, Lock, ChevronRight, Shuffle,
} from 'lucide-react';
import MOCK_CASES from '../data/mockCases';
import { HISTORY_COLUMNS, PHYSICAL_COLUMNS, LABS_COLUMNS } from '../utils/deriveSession';
import { toast } from '../utils/toast';

const FLAGS = [
  ['abn', 'Abnormal'], ['high', 'High'], ['low', 'Low'], ['ok', 'Normal'], ['pending', 'Pending'],
];

const blankCase = () => ({
  id: 'custom',
  title: '',
  presentingSymptom: '',
  demographics: '',
  history: [],
  physical: [],
  labs: [],
  diffAnswer: [],
  cdmAnswer: [],
  diagnosis: { text: '', aliases: [], explanation: '' },
  problemRepresentation: { history: '', physical: '', differential: '', labs: '', cdm: '' },
});

// Aliases edit as comma-separated; multi-line lists as one item per line.
const toCsv = (arr) => (arr || []).join(', ');
const fromCsv = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);
const toLines = (arr) => (arr || []).join('\n');
const fromLines = (s) => s.split('\n').map((x) => x.trim()).filter(Boolean);

const inputCls = 'w-full px-2.5 py-1.5 border border-line rounded-ctl text-meta outline-none focus:ring-2 focus:border-accent focus:ring-accent/25';

const PR_STEPS = [['history', 'History'], ['physical', 'Physical'], ['differential', 'Differential'], ['labs', 'Labs'], ['cdm', 'CDM']];

function Section({ title, count, hint, children, action }) {
  return (
    <section className="border border-line rounded-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-page border-b border-line">
        <h3 className="text-meta font-semibold text-ink uppercase tracking-wide">
          {title}
          {count !== undefined && <span className="ml-1.5 text-ink-2 font-normal normal-case">({count})</span>}
        </h3>
        {action}
      </div>
      {hint && <p className="px-4 pt-2.5 text-meta text-ink-2">{hint}</p>}
      <div className="p-3 space-y-2">{children}</div>
    </section>
  );
}

const AddRowButton = ({ onClick, label }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1 text-meta text-accent hover:text-accent font-medium"
  >
    <Plus className="w-3.5 h-3.5" />{label}
  </button>
);

// History / Physical / Labs share one row shape.
// `keyOptions` switches the row into variant-override mode: pick a skeleton
// finding to override (by key), or leave it on "new finding" to add one.
function FindingRow({ row, columns, withFlag, onChange, onRemove, keyOptions = null }) {
  const overriding = keyOptions ? keyOptions.find((o) => o.key === row.key) : null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 items-start">
      {keyOptions && (
        <select
          className={`${inputCls} sm:col-span-3 bg-surface`}
          value={row.key || ''}
          onChange={(e) => onChange({ ...row, key: e.target.value || undefined })}
          aria-label="Skeleton finding to override"
        >
          <option value="">+ new finding (this variant only)</option>
          {keyOptions.map((o) => <option key={o.key} value={o.key}>overrides: {o.text}</option>)}
        </select>
      )}
      <input
        className={`${inputCls} ${keyOptions ? 'sm:col-span-2' : 'sm:col-span-3'}`}
        value={row.text || ''}
        onChange={(e) => onChange({ ...row, text: e.target.value })}
        placeholder={overriding ? overriding.text : 'Finding / test *'}
      />
      <input
        className={`${inputCls} sm:col-span-4`}
        value={row.detail || ''}
        onChange={(e) => onChange({ ...row, detail: e.target.value })}
        placeholder="Patient's answer / result"
      />
      {!overriding && (
        <select
          className={`${inputCls} sm:col-span-2 bg-surface`}
          value={row.category || columns[0].id}
          onChange={(e) => onChange({ ...row, category: e.target.value })}
        >
          {columns.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      )}
      {withFlag && (
        <select
          className={`${inputCls} sm:col-span-1 bg-surface`}
          value={row.flag || 'abn'}
          onChange={(e) => onChange({ ...row, flag: e.target.value })}
        >
          {FLAGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      )}
      <input
        className={`${inputCls} ${withFlag ? 'sm:col-span-1' : 'sm:col-span-2'}`}
        value={toCsv(row.aliases)}
        onChange={(e) => onChange({ ...row, aliases: fromCsv(e.target.value) })}
        placeholder="aliases"
        title="Comma-separated words students might type. Add a section catch-all (e.g. 'vitals'). Avoid generic filler."
      />
      <button onClick={onRemove} className="sm:col-span-1 justify-self-start p-1.5 text-ink-2/50 hover:text-flag-red" aria-label={`Remove ${row.text || 'row'}`}>
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function CaseManagerScreen({
  customCases, loading, onSaveCase, onDeleteCase, onStartCase, onClose,
}) {
  const [selected, setSelected] = useState(null); // { source: 'builtin'|'custom'|'new', id }
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  // null = the shared skeleton; otherwise the id of the variant being edited.
  const [editingStream, setEditingStream] = useState(null);

  const builtins = MOCK_CASES;
  const isCustom = selected?.source === 'custom';
  const isBuiltin = selected?.source === 'builtin';

  const openCase = (source, caseDef) => {
    setSelected({ source, id: caseDef.id });
    setDraft(JSON.parse(JSON.stringify(caseDef)));
    setEditingStream(null);
    setDirty(false);
  };
  const openNew = () => {
    setSelected({ source: 'new', id: null });
    setDraft(blankCase());
    setEditingStream(null);
    setDirty(false);
  };

  const streams = draft?.streams || [];
  const streamIdx = editingStream ? streams.findIndex((s) => s.id === editingStream) : -1;
  // Every list editor below writes to the "target": the skeleton, or the
  // variant currently being edited (where its lists are overrides, not the
  // whole answer key).
  const target = streamIdx >= 0 ? streams[streamIdx] : draft;

  const setTarget = (updater) => {
    setDraft((d) => {
      if (streamIdx < 0) return updater(d);
      const next = [...(d.streams || [])];
      next[streamIdx] = updater(next[streamIdx] || {});
      return { ...d, streams: next };
    });
    setDirty(true);
  };

  const patch = (changes) => setTarget((t) => ({ ...t, ...changes }));
  const listOf = (key) => (target?.[key] || []);
  const patchList = (key, i, value) => setTarget((t) => {
    const next = [...(t[key] || [])];
    next[i] = value;
    return { ...t, [key]: next };
  });
  const addToList = (key, blank) => setTarget((t) => ({ ...t, [key]: [...(t[key] || []), blank] }));
  const removeFromList = (key, i) => setTarget((t) => ({ ...t, [key]: (t[key] || []).filter((_, j) => j !== i) }));

  // Skeleton entries a variant can override, for the row pickers.
  const keyOptionsFor = (key) => (streamIdx < 0 ? null : (draft[key] || []).map((e) => ({
    key: e.key || e.text || e.etiologicalClass || '',
    text: e.text || e.etiologicalClass || '(untitled)',
  })).filter((o) => o.key));

  const patchStreams = (next) => { setDraft((d) => ({ ...d, streams: next })); setDirty(true); };
  const addStream = () => {
    const id = `variant-${(streams.length + 1)}-${Date.now().toString(36).slice(-4)}`;
    patchStreams([...streams, { id, label: '', summary: '', omit: [] }]);
    setEditingStream(id);
  };
  const removeStream = (id) => {
    patchStreams(streams.filter((s) => s.id !== id));
    if (editingStream === id) setEditingStream(null);
  };

  const valid = draft?.title?.trim() && draft?.presentingSymptom?.trim();

  const save = async () => {
    if (!valid) { toast('A title and presenting symptom are required.', 'info'); return; }
    // Editing a built-in forks it into the facilitator's own library.
    const id = isCustom ? selected.id : undefined;
    const newId = await onSaveCase(draft, id);
    setSelected({ source: 'custom', id: newId });
    setDirty(false);
    toast(isCustom ? 'Case saved.' : 'Saved as a copy in your cases.', 'success');
  };

  const stats = (c) =>
    `${(c.history || []).length} Hx · ${(c.physical || []).length} Px · ${(c.labs || []).length} labs · ${(c.cdmAnswer || []).length} dx`
    + ((c.streams || []).length ? ` · ${c.streams.length} variants` : '');

  const listItem = (c, source) => {
    const active = selected?.id === c.id && selected?.source === source;
    return (
      <button
        key={`${source}-${c.id}`}
        onClick={() => openCase(source, c)}
        className={`w-full text-left px-3 py-2 rounded-ctl transition border ${active ? 'bg-accent-tint border-accent' : 'border-transparent hover:bg-accent-tint'}`}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="text-body font-medium text-ink truncate">{c.title || 'Untitled case'}</span>
          {source === 'builtin' && <Lock className="w-3 h-3 text-ink-2 flex-shrink-0" title="Built-in, editing saves a copy" />}
        </span>
        <span className="block text-meta text-ink-2 mt-0.5">{c.presentingSymptom} · {stats(c)}</span>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-page flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-3 bg-surface border-b border-line flex-shrink-0">
        <h1 className="text-heading font-medium text-ink flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-accent" />Case library
        </h1>
        <button onClick={onClose} className="p-2 rounded-ctl hover:bg-accent-tint text-ink-2" aria-label="Close case library">
          <X className="w-5 h-5" />
        </button>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Case list */}
        <aside className="lg:w-72 flex-shrink-0 bg-surface border-b lg:border-b-0 lg:border-r border-line overflow-y-auto p-3 max-h-56 lg:max-h-none">
          <button
            onClick={openNew}
            className="w-full flex items-center justify-center gap-1.5 mb-3 bg-accent text-white py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition"
          >
            <FilePlus2 className="w-4 h-4" />New case
          </button>
          <h2 className="text-meta font-semibold text-ink-2 uppercase tracking-wide px-1 mb-1">My cases ({customCases.length})</h2>
          {loading && <p className="text-meta text-ink-2 px-1 py-2">Loading…</p>}
          {!loading && !customCases.length && <p className="text-meta text-ink-2 px-1 py-1">None yet, edit a built-in or start a new one.</p>}
          <div className="space-y-0.5 mb-4">{customCases.map((c) => listItem(c, 'custom'))}</div>
          <h2 className="text-meta font-semibold text-ink-2 uppercase tracking-wide px-1 mb-1">Built-in ({builtins.length})</h2>
          <div className="space-y-0.5">{builtins.map((c) => listItem(c, 'builtin'))}</div>
        </aside>

        {/* Editor */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {!draft ? (
            <div className="h-full flex items-center justify-center text-center text-ink-2">
              <div>
                <BookOpen className="w-10 h-10 mx-auto mb-3 text-ink-2/50" />
                <p className="text-body">Pick a case to edit, or create a new one.</p>
                <p className="text-meta mt-1 max-w-sm">Everything for a case, history, exam, labs, expert differential and the final diagnosis, lives on one page.</p>
              </div>
            </div>
          ) : (
            <div className="max-w-5xl mx-auto space-y-4 pb-24">
              {isBuiltin && (
                <p className="flex items-center gap-1.5 text-meta text-flag-amber bg-flag-amber/5 border border-flag-amber/40 rounded-ctl px-3 py-2">
                  <Copy className="w-3.5 h-3.5 flex-shrink-0" />
                  Built-in case, edits are saved as your own copy, leaving the original intact.
                </p>
              )}

              {/* Variant switcher, the sections below edit whatever is picked
                  here: the shared skeleton, or one diagnostic variant. */}
              <div className="flex flex-wrap items-center gap-1.5 bg-surface border border-line rounded-card px-3 py-2">
                <span className="flex items-center gap-1.5 text-meta font-semibold text-ink-2 uppercase tracking-wide mr-1">
                  <Shuffle className="w-3.5 h-3.5 text-accent" />Editing
                </span>
                <button
                  onClick={() => setEditingStream(null)}
                  className={`px-2.5 py-1 rounded-ctl text-meta font-medium border transition ${streamIdx < 0 ? 'bg-accent-tint border-accent text-accent' : 'border-line text-ink-2 hover:bg-accent-tint'}`}
                >
                  Shared skeleton
                </button>
                {streams.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setEditingStream(s.id)}
                    className={`px-2.5 py-1 rounded-ctl text-meta font-medium border transition ${editingStream === s.id ? 'bg-accent-tint border-accent text-accent' : 'border-line text-ink-2 hover:bg-accent-tint'}`}
                  >
                    {s.label || `Variant ${i + 1}`}
                  </button>
                ))}
                <button onClick={addStream} className="flex items-center gap-1 px-2.5 py-1 rounded-ctl text-meta font-medium text-accent hover:bg-accent-tint transition">
                  <Plus className="w-3.5 h-3.5" />Add variant
                </button>
              </div>

              {streamIdx < 0 ? (
                <Section title="Case details" hint={streams.length ? `This case has ${streams.length} diagnostic variant${streams.length === 1 ? '' : 's'}, the facilitator picks which to run, or queues several for one lecture.` : undefined}>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input className={inputCls} value={draft.title} onChange={(e) => patch({ title: e.target.value })} placeholder="Case title *" />
                    <input className={inputCls} value={draft.presentingSymptom} onChange={(e) => patch({ presentingSymptom: e.target.value })} placeholder="Presenting symptom *" />
                  </div>
                  <input className={inputCls} value={draft.demographics} onChange={(e) => patch({ demographics: e.target.value })} placeholder="One-line patient description (shown as 'Pt:' in the case summary)" />
                </Section>
              ) : (
                <Section
                  title="Variant details"
                  hint="Everything below is a DIFF against the shared skeleton: pick a skeleton finding to override its result, or add findings that only exist in this variant. Anything you don't touch is inherited."
                  action={(
                    <button onClick={() => removeStream(target.id)} className="flex items-center gap-1 text-meta text-ink-2 hover:text-flag-red">
                      <Trash2 className="w-3.5 h-3.5" />Delete variant
                    </button>
                  )}
                >
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input className={inputCls} value={target.label || ''} onChange={(e) => patch({ label: e.target.value })} placeholder="Variant label (e.g. Pre-renal: fluid-responsive)" />
                    <input className={inputCls} value={target.summary || ''} onChange={(e) => patch({ summary: e.target.value })} placeholder="One-line summary for the facilitator's picker" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input className={inputCls} value={target.demographics || ''} onChange={(e) => patch({ demographics: e.target.value })} placeholder="Override the patient description (optional)" />
                    <input className={inputCls} value={toCsv(target.omit)} onChange={(e) => patch({ omit: fromCsv(e.target.value) })} placeholder="Skeleton findings to drop, comma-separated keys" />
                  </div>
                  <p className="text-meta text-flag-amber">
                    The variant label names the answer, so students only ever see “Variant 2 of 3”.
                  </p>
                </Section>
              )}

              <Section
                title="Problem representation"
                hint="One handover per step: what you would say aloud to a colleague taking over the patient, using only what has been revealed up to that step. Start from the patient and build forward; compress earlier detail as later detail arrives, 3 to 4 sentences. The panel shows the latest revealed step's statement; a variant inherits any step it leaves blank."
              >
                <div className="space-y-2">
                  {PR_STEPS.map(([step, label]) => (
                    <label key={step} className="block">
                      <span className="text-meta font-semibold text-ink-2 uppercase tracking-wide">{label}</span>
                      <textarea
                        rows={3}
                        className={`${inputCls} resize-y mt-1`}
                        value={target.problemRepresentation?.[step] || ''}
                        onChange={(e) => patch({ problemRepresentation: { ...(target.problemRepresentation || {}), [step]: e.target.value } })}
                        placeholder={`After ${label}: "X-year-old [sex] with a history of ___ presented with ___. On examination ___." Cumulative, only what this step has revealed, 3 to 4 sentences.`}
                      />
                    </label>
                  ))}
                </div>
              </Section>

              <Section
                title="History findings"
                count={listOf('history').length}
                hint="What the patient reveals when asked. Aliases are what students might type, include a section catch-all like 'past medical history'."
                action={<AddRowButton label="Add finding" onClick={() => addToList('history', { text: '', detail: '', aliases: [], category: 'hpi' })} />}
              >
                {listOf('history').map((row, i) => (
                  <FindingRow key={i} row={row} columns={HISTORY_COLUMNS} keyOptions={keyOptionsFor('history')}
                    onChange={(v) => patchList('history', i, v)} onRemove={() => removeFromList('history', i)} />
                ))}
                {!listOf('history').length && <p className="text-meta text-ink-2">No history findings yet.</p>}
              </Section>

              <Section
                title="Physical findings"
                count={listOf('physical').length}
                hint="Grouped by exam system. A student writing 'vitals' matches every finding carrying that alias."
                action={<AddRowButton label="Add finding" onClick={() => addToList('physical', { text: '', detail: '', aliases: [], category: 'vitals' })} />}
              >
                {listOf('physical').map((row, i) => (
                  <FindingRow key={i} row={row} columns={PHYSICAL_COLUMNS} keyOptions={keyOptionsFor('physical')}
                    onChange={(v) => patchList('physical', i, v)} onRemove={() => removeFromList('physical', i)} />
                ))}
                {!listOf('physical').length && <p className="text-meta text-ink-2">No exam findings yet.</p>}
              </Section>

              <Section
                title="Labs & investigations"
                count={listOf('labs').length}
                hint="Use the Pending flag for confirmatory tests so ordering them doesn't give away the diagnosis. Tests not listed here read 'Normal / negative' when ordered."
                action={<AddRowButton label="Add test" onClick={() => addToList('labs', { text: '', detail: '', aliases: [], category: 'bloods', flag: 'abn' })} />}
              >
                {listOf('labs').map((row, i) => (
                  <FindingRow key={i} row={row} columns={LABS_COLUMNS} withFlag keyOptions={keyOptionsFor('labs')}
                    onChange={(v) => patchList('labs', i, v)} onRemove={() => removeFromList('labs', i)} />
                ))}
                {!listOf('labs').length && <p className="text-meta text-ink-2">No investigations yet.</p>}
              </Section>

              <Section
                title="Expert differential"
                count={listOf('diffAnswer').length}
                hint="Autofilled when the facilitator reveals the Differential step. One block per etiological class; one item per line."
                action={<AddRowButton label="Add class" onClick={() => addToList('diffAnswer', { etiologicalClass: '', aliases: [], diseases: [], supporting: [], opposing: [] })} />}
              >
                {listOf('diffAnswer').map((row, i) => (
                  <div key={i} className="border border-line rounded-ctl p-2.5 space-y-1.5">
                    <div className="flex gap-1.5">
                      <input className={`${inputCls} flex-1`} value={row.etiologicalClass || ''} onChange={(e) => patchList('diffAnswer', i, { ...row, etiologicalClass: e.target.value })} placeholder={row.key ? `overrides: ${row.key}` : 'Etiological class (e.g. Cardiovascular)'} />
                      <input className={`${inputCls} flex-1`} value={toCsv(row.aliases)} onChange={(e) => patchList('diffAnswer', i, { ...row, aliases: fromCsv(e.target.value) })} placeholder="aliases (cardiac, heart)" />
                      <button onClick={() => removeFromList('diffAnswer', i)} className="p-1.5 text-ink-2/50 hover:text-flag-red" aria-label="Remove class"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-1.5">
                      {['diseases', 'supporting', 'opposing'].map((k) => (
                        <textarea key={k} rows={3} className={`${inputCls} resize-y`} value={toLines(row[k])}
                          onChange={(e) => patchList('diffAnswer', i, { ...row, [k]: fromLines(e.target.value) })}
                          placeholder={`${k[0].toUpperCase()}${k.slice(1)}, one per line`} />
                      ))}
                    </div>
                  </div>
                ))}
                {!listOf('diffAnswer').length && <p className="text-meta text-ink-2">No expert differential yet.</p>}
              </Section>

              <Section
                title="Expert CDM answer"
                count={listOf('cdmAnswer').length}
                hint="Autofilled into the CDM grid on the final reveal, only diagnoses the class missed are added."
                action={<AddRowButton label="Add diagnosis" onClick={() => addToList('cdmAnswer', { text: '', aliases: [], isCritical: false, supporting: [], opposing: [] })} />}
              >
                {listOf('cdmAnswer').map((row, i) => (
                  <div key={i} className="border border-line rounded-ctl p-2.5 space-y-1.5">
                    <div className="flex gap-1.5 items-center">
                      <input className={`${inputCls} flex-1`} value={row.text || ''} onChange={(e) => patchList('cdmAnswer', i, { ...row, text: e.target.value })} placeholder={row.key ? `overrides: ${row.key}` : 'Diagnosis'} />
                      <input className={`${inputCls} flex-1`} value={toCsv(row.aliases)} onChange={(e) => patchList('cdmAnswer', i, { ...row, aliases: fromCsv(e.target.value) })} placeholder="aliases (pe, pulmonary embolism)" />
                      <label className="flex items-center gap-1 text-meta text-flag-red whitespace-nowrap">
                        <input type="checkbox" className="accent-flag-red" checked={!!row.isCritical} onChange={(e) => patchList('cdmAnswer', i, { ...row, isCritical: e.target.checked })} />
                        Critical
                      </label>
                      <button onClick={() => removeFromList('cdmAnswer', i)} className="p-1.5 text-ink-2/50 hover:text-flag-red" aria-label="Remove diagnosis"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-1.5">
                      {['supporting', 'opposing'].map((k) => (
                        <textarea key={k} rows={2} className={`${inputCls} resize-y`} value={toLines(row[k])}
                          onChange={(e) => patchList('cdmAnswer', i, { ...row, [k]: fromLines(e.target.value) })}
                          placeholder={`${k[0].toUpperCase()}${k.slice(1)} evidence, one per line`} />
                      ))}
                    </div>
                  </div>
                ))}
                {!listOf('cdmAnswer').length && <p className="text-meta text-ink-2">No expert diagnoses yet.</p>}
              </Section>

              <Section title="Final diagnosis" hint="Revealed at the end. Cards matching this are flagged in the CDM grid.">
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className={inputCls} value={target.diagnosis?.text || ''} onChange={(e) => patch({ diagnosis: { ...target.diagnosis, text: e.target.value } })} placeholder="Final diagnosis" />
                  <input className={inputCls} value={toCsv(target.diagnosis?.aliases)} onChange={(e) => patch({ diagnosis: { ...target.diagnosis, aliases: fromCsv(e.target.value) } })} placeholder="aliases" />
                </div>
                <textarea rows={3} className={`${inputCls} resize-y`} value={target.diagnosis?.explanation || ''} onChange={(e) => patch({ diagnosis: { ...target.diagnosis, explanation: e.target.value } })} placeholder="Teaching explanation shown with the reveal" />
              </Section>
            </div>
          )}
        </main>
      </div>

      {/* Action bar */}
      {draft && (
        <footer className="flex-shrink-0 bg-surface border-t border-line px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <span className="text-meta text-ink-2">
            {dirty ? 'Unsaved changes' : isCustom ? 'Saved' : isBuiltin ? 'Built-in case' : 'New case'}
            {!valid && <span className="text-flag-amber"> · title and presenting symptom required</span>}
          </span>
          <div className="flex items-center gap-2">
            {isCustom && (
              <button onClick={() => { onDeleteCase(selected.id); setDraft(null); setSelected(null); }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-ctl text-meta text-ink-2 hover:text-flag-red hover:bg-flag-red/5 transition">
                <Trash2 className="w-4 h-4" />Delete
              </button>
            )}
            <button onClick={save} disabled={!valid}
              className="flex items-center gap-1.5 bg-accent text-white px-4 py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition disabled:opacity-50">
              <Save className="w-4 h-4" />{isCustom ? 'Save changes' : 'Save to my cases'}
            </button>
            <button
              onClick={() => {
                // Editing a variant starts that variant; from the skeleton,
                // every variant is queued for the lecture.
                const streamIds = streamIdx >= 0 ? [target.id] : streams.map((s) => s.id);
                onStartCase(draft, { streamIds });
                onClose();
              }}
              disabled={!valid}
              className="flex items-center gap-1.5 bg-accent text-white px-4 py-2 rounded-ctl text-meta font-medium hover:bg-accent-strong transition disabled:opacity-50"
              title={streamIdx >= 0 ? 'Load just this variant into the current session' : 'Load this case into the current session'}
            >
              <PlayCircle className="w-4 h-4" />
              {streamIdx >= 0 ? 'Start this variant' : 'Start this case'}
              {streamIdx < 0 && streams.length > 1 && <span className="opacity-80">({streams.length} variants)</span>}
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
